import { useState, useEffect } from 'react';
import cx from 'classnames';
import { CheckboxFinalFormAdapter } from 'components/Checkbox/Checkbox';
import Field from 'components/Field/Field';
import { InputFinalFormAdapter } from 'components/Input/Input';
import { get } from 'es-toolkit/compat';

type InputCheckboxValue = {
  checkboxValue?: boolean;
  inputValue?: unknown;
};

type FieldValidator = (
  value: unknown,
  allValues: object,
  meta?: unknown,
) => unknown;

type FieldProps = {
  placeholder?: string;
  validate?: FieldValidator;
  [key: string]: unknown;
};

type Props = {
  form: {
    getState: () => { values: Record<string, unknown> };
    change: (field: string, value: unknown) => void;
  };
  name: string;
  inputProps?: FieldProps;
  checkboxProps?: FieldProps;
  hideCheckboxIfValue?: boolean;
};

function InputCheckbox({
  form,
  name,
  inputProps = {},
  checkboxProps = {},
  hideCheckboxIfValue = true,
}: Props) {
  const lowerCheckboxThreshold = 15;
  const placeholderLength = inputProps.placeholder?.length ?? 0;
  const [lowerCheckbox, setLowerCheckbox] = useState(
    placeholderLength < lowerCheckboxThreshold,
  );
  const { values } = form.getState();

  useEffect(() => {
    setLowerCheckbox(placeholderLength > lowerCheckboxThreshold);
  }, [placeholderLength]);

  const currentValue = get(values, name) as InputCheckboxValue | undefined;
  const truthy = !!currentValue?.checkboxValue;

  useEffect(() => {
    // We need to remove value if we don't hide the checkbox when there is a value selected
    if (truthy && !hideCheckboxIfValue) {
      form.change(`${name}.inputValue`, null);
    }
  }, [truthy, hideCheckboxIfValue]);

  return (
    <div>
      <div className={cx('relative', { 'pb-xl sm:pb-0': lowerCheckbox })}>
        <Field
          disabled={truthy}
          applyDisabledStyle={truthy}
          name={`${name}.inputValue`}
          component={InputFinalFormAdapter}
          {...inputProps}
          validate={truthy ? () => undefined : inputProps.validate}
          key={truthy ? `${name}.inputValue0` : `${name}.inputValue1`}
          nestedField
        />
        <div
          className={cx(
            'relative text-right',
            // the checkbox carries a built-in 40px top margin (alignWithField);
            // pull it up so the gap below the input is 20px on mobile
            { 'bottom-0 -mt-sm sm:mt-0 sm:bottom-sm': lowerCheckbox },
            {
              hidden: hideCheckboxIfValue && !!currentValue?.inputValue,
            },
          )}
        >
          <Field
            useOnChangeFormToHandle
            name={`${name}.checkboxValue`}
            label="I don't know."
            {...checkboxProps}
            component={CheckboxFinalFormAdapter}
            nestedField
          />
        </div>
      </div>
    </div>
  );
}

type InputCheckboxAdapterProps = Omit<Props, 'name'> & {
  input: {
    name: string;
  };
};

export const InputCheckboxAdapter = ({
  input,
  ...rest
}: InputCheckboxAdapterProps) => <InputCheckbox {...rest} {...input} />;
